/**
 * ColumnFilterPopover - Выпадающее окно фильтра для колонки таблицы
 *
 * Excel-style фильтр с чекбоксами для выбора значений.
 * Включает:
 * - Поиск по значениям
 * - Кнопки "Выбрать все" / "Снять все"
 * - Список чекбоксов с уникальными значениями
 * - Опция "(Пустые)" для null/пустых значений
 */

import React, { useState, useMemo } from 'react';
import {
  Stack,
  TextInput,
  Group,
  Button,
  Divider,
  Checkbox,
  ScrollArea,
  Text,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';

// Специальное значение для пустых ячеек
const EMPTY_VALUE = '__empty__';

/**
 * Компонент выпадающего окна фильтра
 */
const ColumnFilterPopover = ({
  column,
  data,
  filter,
  onChange,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Собираем уникальные значения из данных
  const uniqueValues = useMemo(() => {
    // Если у колонки есть предустановленные опции, используем их
    if (column.filterOptions) {
      return column.filterOptions;
    }

    // Собираем уникальные значения из данных
    const valuesMap = new Map();
    let hasEmpty = false;

    data.forEach((row) => {
      const rawValue = row[column.key];

      // Проверяем на пустое значение
      if (rawValue == null || rawValue === '') {
        hasEmpty = true;
        return;
      }

      // Получаем отображаемое значение
      const displayValue = column.filterDisplayValue
        ? column.filterDisplayValue(rawValue, row)
        : rawValue;

      // Используем сырое значение как ключ, отображаемое как label
      if (!valuesMap.has(rawValue)) {
        valuesMap.set(rawValue, String(displayValue));
      }
    });

    // Преобразуем в массив и сортируем
    const result = Array.from(valuesMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ru'));

    // Добавляем опцию "(Пустые)" в начало, если есть пустые значения
    if (hasEmpty) {
      result.unshift({ value: EMPTY_VALUE, label: '(Пустые)' });
    }

    return result;
  }, [data, column]);

  // Фильтруем по поисковому запросу
  const filteredValues = useMemo(() => {
    if (!searchQuery.trim()) return uniqueValues;

    const query = searchQuery.toLowerCase().trim();
    return uniqueValues.filter((item) =>
      item.label.toLowerCase().includes(query)
    );
  }, [uniqueValues, searchQuery]);

  // Проверяем, выбрано ли значение
  // Если filter пустой (size === 0), значит выбраны ВСЕ значения
  // Если filter содержит '__none__', значит ничего не выбрано
  const isValueSelected = (value) => {
    if (filter.size === 0) return true;
    if (filter.has('__none__')) return false;
    return filter.has(value);
  };

  // Подсчёт выбранных значений
  const hasNoneSelected = filter.has('__none__');
  const selectedCount = filter.size === 0 ? uniqueValues.length : (hasNoneSelected ? 0 : filter.size);
  const allSelected = filter.size === 0;

  // Обработчик переключения чекбокса
  const handleToggle = (value) => {
    const newFilter = new Set(filter);

    if (filter.size === 0) {
      // Если сейчас выбраны все, переключаемся на "все кроме этого"
      uniqueValues.forEach((item) => {
        if (item.value !== value) {
          newFilter.add(item.value);
        }
      });
    } else if (filter.has('__none__')) {
      // Если ничего не выбрано, выбираем только это значение
      newFilter.clear();
      newFilter.add(value);
    } else if (newFilter.has(value)) {
      // Убираем значение из фильтра
      newFilter.delete(value);
      // Если убрали последнее, показываем "ничего"
      if (newFilter.size === 0) {
        newFilter.add('__none__'); // Специальное значение - ничего не показывать
      }
    } else {
      // Добавляем значение в фильтр
      newFilter.add(value);
      // Если выбрали все, возвращаем пустой Set
      if (newFilter.size === uniqueValues.length) {
        newFilter.clear();
      }
    }

    onChange(newFilter);
  };

  // Выбрать все
  const handleSelectAll = () => {
    onChange(new Set()); // Пустой Set = показать все
  };

  // Снять все
  const handleClearAll = () => {
    // Создаём Set с невозможным значением, чтобы ничего не показать
    onChange(new Set(['__none__']));
  };

  return (
    <Stack gap="xs" style={{ width: 250 }}>
      {/* Поиск */}
      <TextInput
        placeholder="Поиск..."
        size="xs"
        leftSection={<IconSearch size={14} />}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        radius="xl"
      />

      {/* Кнопки управления */}
      <Group gap="xs">
        <Button
          size="xs"
          variant="subtle"
          onClick={handleSelectAll}
          disabled={allSelected}
        >
          Выбрать все
        </Button>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleClearAll}
          disabled={selectedCount === 0}
        >
          Снять все
        </Button>
      </Group>

      <Divider />

      {/* Счётчик выбранных */}
      <Text size="xs" c="dimmed">
        Выбрано: {selectedCount} из {uniqueValues.length}
      </Text>

      {/* Список чекбоксов */}
      <ScrollArea.Autosize mah={200}>
        <Stack gap={4}>
          {filteredValues.length === 0 ? (
            <Text size="xs" c="dimmed" ta="center" py="sm">
              Ничего не найдено
            </Text>
          ) : (
            filteredValues.map(({ value, label }) => (
              <Checkbox
                key={String(value)}
                label={
                  <Text size="xs" style={{ wordBreak: 'break-word' }}>
                    {label}
                  </Text>
                }
                size="xs"
                checked={isValueSelected(value)}
                onChange={() => handleToggle(value)}
                styles={{
                  body: { alignItems: 'flex-start' },
                  label: { paddingLeft: 8 },
                }}
              />
            ))
          )}
        </Stack>
      </ScrollArea.Autosize>
    </Stack>
  );
};

export default ColumnFilterPopover;
